/*
 * <heritage-quick-add>
 *
 * Grid-card quick-add control for signed-in customers. Adds or
 * removes a single-variant product without leaving the collection
 * page, mirroring the cart's "Request on Memo / Purchase Now" pair
 * but as upstream staging.
 *
 * State machine (host carries [data-quick-add-state]):
 *
 *           ┌──────────────────────────────── timeout 900ms ────────┐
 *           ▼                                                       │
 *   idle ─click→ added ───settle─→ in-cart ─hover (mouse) ──┐       │
 *                                       │                  │       │
 *                                       │                  ▼       │
 *                                       │             [CSS-only    │
 *                                       │              Remove tip] │
 *                                       │                  │       │
 *                                       │                  └─click─┴─→ remove
 *                                       │                                 │
 *                                       └─tap (touch) ─→ remove ──click───┘
 *                                                          │
 *                                                          └─ click → actually removes → idle
 *
 * Wire format — uses the existing theme cart endpoints to avoid
 * introducing a parallel cart system:
 *   ADD:    POST /cart/add.js   { id: variantId, quantity: 1 }
 *           then GET /cart.js for the live cart payload
 *   REMOVE: POST /cart/change.js { id: variantId, quantity: 0 }
 *           (the response IS the new cart)
 *
 * After each successful mutation, dispatches `mdj:cart-updated` on
 * document carrying the cart payload. The header badge updater
 * (sections/header.liquid) and the PDP form refresher
 * (assets/heritage-pdp-form.js) both listen for this event and
 * react automatically. We deliberately do NOT dispatch the
 * `mdj:cart-add-pending` / `mdj:cart-add-success` pair the PDP form
 * uses, because those open the global <heritage-cart-confirm> modal
 * — and the grid quick-add should be a quiet, no-modal staging action.
 *
 * Multi-variant or unavailable products do not render this component
 * — the Liquid snippet renders a PDP-link fallback instead. So we
 * never need to disambiguate variants client-side.
 */

class HeritageQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.btn = this.querySelector('[data-quick-add-btn]');
    this.liveEl = this.querySelector('[data-quick-add-live]');

    this.variantId = this.dataset.variantId || '';
    this.productId = this.dataset.productId || '';
    this.productTitle = this.dataset.productTitle || 'item';
    this.productUrl = this.dataset.productUrl || '';

    // Hover-capable device detection. On desktop the in-cart button's
    // CSS :hover reveals "Remove" visually without state change, so a
    // single click on the visible Remove label performs the remove.
    // On touch, that hover never fires — instead the first tap arms
    // (sets state to "remove"), a second tap performs.
    this.isHoverDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (this.btn) this.btn.addEventListener('click', this.handleClick);

    // Listen for any other surface mutating the cart (PDP add, cart
    // page qty change, memo submit clearing the cart) so this card
    // stays accurate.
    document.addEventListener('mdj:cart-updated', this.handleCartUpdated);
    document.addEventListener('mdj:cart-add-success', this.handleCartUpdated);
    document.addEventListener('mdj:cart-cleared', this.handleCartCleared.bind(this));
  }

  disconnectedCallback() {
    if (this.btn) this.btn.removeEventListener('click', this.handleClick);
    document.removeEventListener('mdj:cart-updated', this.handleCartUpdated);
    document.removeEventListener('mdj:cart-add-success', this.handleCartUpdated);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  get state() {
    return this.dataset.quickAddState || 'idle';
  }

  setState(next) {
    this.dataset.quickAddState = next;
    this.updateAriaLabel(next);
  }

  updateAriaLabel(state) {
    if (!this.btn) return;
    const title = this.productTitle;
    let label = '';
    if (state === 'idle') label = `Add ${title} to cart.`;
    else if (state === 'added') label = `${title} added.`;
    else if (state === 'in-cart') label = `${title} is in your cart. Click to remove.`;
    else if (state === 'remove') label = `Remove ${title} from cart.`;
    this.btn.setAttribute('aria-label', label);
  }

  announce(message) {
    if (!this.liveEl) return;
    // Clear first so the same message announced twice in a row still
    // fires for screen readers.
    this.liveEl.textContent = '';
    window.setTimeout(() => { this.liveEl.textContent = message; }, 30);
  }

  setBusy(busy) {
    if (!this.btn) return;
    if (busy) this.btn.dataset.busy = 'true';
    else this.btn.removeAttribute('data-busy');
  }

  // ── Click routing ──

  handleClick(evt) {
    evt.preventDefault();
    const state = this.state;

    if (state === 'idle')           return this.performAdd();
    if (state === 'remove')         return this.performRemove();
    if (state === 'in-cart') {
      if (this.isHoverDevice)       return this.performRemove();
      return this.armRemove();
    }
    // 'added' is transient + non-interactive (pointer-events: none) so
    // this branch should be unreachable, but be defensive.
  }

  // ── ARM (touch only) — first tap on in-cart reveals Remove + waits ──

  armRemove() {
    this.setState('remove');
    this.announce('Tap again to remove.');
    // Outside-tap reverts so the user can dismiss without committing.
    document.addEventListener('click', this.handleOutsideClick);
    // Auto-revert if no second tap arrives in 4s.
    if (this._armTimer) clearTimeout(this._armTimer);
    this._armTimer = window.setTimeout(() => this.disarmRemove(), 4000);
  }

  disarmRemove() {
    if (this.state !== 'remove') return;
    this.setState('in-cart');
    document.removeEventListener('click', this.handleOutsideClick);
    if (this._armTimer) { clearTimeout(this._armTimer); this._armTimer = null; }
  }

  handleOutsideClick(evt) {
    if (this.contains(evt.target)) return;
    this.disarmRemove();
  }

  // ── Network ops ──

  async performAdd() {
    if (!this.variantId) return;
    this.setBusy(true);

    try {
      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        credentials: 'same-origin',
      });
      if (!addRes.ok) throw new Error(`add failed: ${addRes.status}`);

      // Pull the live cart so the badge update event carries the
      // authoritative item count.
      const cart = await this.fetchCart();
      this.broadcast(cart);

      // Transient confirmation before settling.
      this.setState('added');
      this.announce(`${this.productTitle} added to cart.`);
      window.setTimeout(() => {
        if (this.state === 'added') this.setState('in-cart');
      }, 900);
    } catch (err) {
      console.error('quick-add: add failed', err);
      this.announce('Add to cart failed. Please try again.');
    } finally {
      this.setBusy(false);
    }
  }

  async performRemove() {
    if (!this.variantId) return;
    this.setBusy(true);
    document.removeEventListener('click', this.handleOutsideClick);
    if (this._armTimer) { clearTimeout(this._armTimer); this._armTimer = null; }

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: this.variantId, quantity: 0 }),
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`remove failed: ${res.status}`);
      const cart = await res.json();
      this.broadcast(cart);

      this.setState('idle');
      this.announce(`${this.productTitle} removed from cart.`);
    } catch (err) {
      console.error('quick-add: remove failed', err);
      this.announce('Remove from cart failed. Please try again.');
      // Revert visual state so the user isn't stuck on "remove".
      this.setState('in-cart');
    } finally {
      this.setBusy(false);
    }
  }

  async fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`cart fetch failed: ${res.status}`);
    return res.json();
  }

  broadcast(cart) {
    document.dispatchEvent(new CustomEvent('mdj:cart-updated', { detail: { cart } }));
  }

  // ── React to other surfaces' cart mutations ──

  handleCartUpdated(evt) {
    const cart = evt && evt.detail && evt.detail.cart;
    if (!cart) return;
    const inCart = (cart.items || []).some(
      (it) => String(it.product_id) === String(this.productId)
    );
    // Don't stomp on a transient "added" flash from this card's own add.
    if (this.state === 'added') return;
    // Don't stomp on an armed "remove" state if it's still arming —
    // the user is mid-decision.
    if (this.state === 'remove' && inCart) return;
    this.setState(inCart ? 'in-cart' : 'idle');
  }

  handleCartCleared() {
    this.setState('idle');
  }
}

if (!customElements.get('heritage-quick-add')) {
  customElements.define('heritage-quick-add', HeritageQuickAdd);
}
